<template>
    <div class="w-full">
        <!-- Processing state (tool still executing) -->
        <div v-if="isProcessing" class="rounded-lg border border-slate-200 bg-white overflow-hidden shadow-sm">
            <div class="px-4 py-3 flex items-center gap-2 text-sm text-slate-500">
                <UIcon name="i-heroicons-arrow-path" class="w-4 h-4 animate-spin text-blue-500" />
                <span>Analyzing contact<span v-if="progressText" class="text-slate-400"> ({{ progressText }})</span>...</span>
            </div>
            <div v-if="inputAnalysisPreview" class="px-4 pb-3">
                <div class="text-xs text-slate-400 italic line-clamp-2">{{ inputAnalysisPreview }}</div>
            </div>
        </div>

        <!-- Error state -->
        <div v-else-if="hasError" class="rounded-lg border border-red-200 bg-red-50 px-4 py-3">
            <div class="flex items-center gap-2 text-sm text-red-700">
                <UIcon name="i-heroicons-x-circle" class="w-4 h-4" />
                <span>{{ part.errorText || 'Failed to load review card' }}</span>
            </div>
        </div>

        <!-- Loading card data from server -->
        <div v-else-if="cardLoading" class="rounded-lg border border-slate-200 bg-white overflow-hidden shadow-sm">
            <div class="px-4 py-3 flex items-center gap-2 text-sm text-slate-500">
                <UIcon name="i-heroicons-arrow-path" class="w-4 h-4 animate-spin text-blue-500" />
                <span>Loading contact details...</span>
            </div>
        </div>

        <!-- Completed: compact review card with structured verdict -->
        <div v-else-if="contact"
            class="rounded-lg border overflow-hidden shadow-sm relative"
            :class="cardBorderClass">
            <span v-if="progressText" class="absolute top-2 right-3 text-[10px] font-medium text-slate-400 bg-white px-1.5 py-0.5 rounded-full border border-slate-100">{{ progressText }}</span>

            <!-- Row 1: Contact Header + Verdict Badge -->
            <div class="px-4 py-2.5 flex items-center gap-2.5 min-w-0 bg-white">
                <img v-if="contact.prospectExtra?.icon"
                    :src="(contact.prospectExtra.icon as string)"
                    class="w-7 h-7 rounded flex-shrink-0"
                    :alt="companyName" />
                <div class="min-w-0 flex-1">
                    <div class="flex items-center gap-1.5 flex-wrap">
                        <span class="font-semibold text-sm text-slate-900">{{ contactNameStr }}</span>
                        <span v-if="contactTitle" class="text-xs text-slate-500">{{ contactTitle }}</span>
                        <span v-if="companyName" class="text-xs text-slate-500">at
                            <a v-if="companyUrl" :href="companyUrl" target="_blank" rel="noopener noreferrer" class="font-medium text-blue-700 hover:text-blue-900 hover:underline">{{ companyName }}</a>
                            <span v-else class="font-medium text-slate-700">{{ companyName }}</span>
                        </span>
                        <a v-if="linkedinUrl" :href="linkedinUrl" target="_blank" class="text-blue-600 hover:text-blue-800">
                            <UIcon name="i-bi-linkedin" class="w-3.5 h-3.5" />
                        </a>
                    </div>
                    <div class="flex items-center gap-1.5 mt-0.5 flex-wrap">
                        <!-- Verdict badge -->
                        <span v-if="verdict" class="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full" :class="verdictBadgeClass">
                            {{ verdictLabel }}
                        </span>
                        <UBadge color="secondary" variant="subtle" size="sm">{{ contact.campaignName }}</UBadge>
                        <UBadge v-if="contact.prospectExtra?.industry" color="neutral" variant="subtle" size="sm">{{ contact.prospectExtra.industry }}</UBadge>
                        <UBadge v-if="contact.prospectExtra?.size" color="neutral" variant="subtle" size="sm">{{ contact.prospectExtra.size }}</UBadge>
                    </div>
                </div>
            </div>

            <!-- Row 2: Verdict reason + angle (always visible, compact) -->
            <div class="px-4 py-2 border-t border-slate-100 bg-white">
                <p v-if="verdictReason" class="text-xs text-slate-700 leading-relaxed">{{ verdictReason }}</p>
                <p v-if="angle" class="text-xs text-slate-500 mt-1 italic">"{{ angle }}"</p>
                <p v-if="suggestion" class="text-xs text-slate-700 mt-1.5">
                    <span class="font-medium" :class="verdict === 'would-reply' ? 'text-green-700' : 'text-blue-700'">
                        {{ verdict === 'would-reply' ? 'What worked:' : 'What would work:' }}
                    </span>
                    {{ suggestion }}
                </p>
            </div>

            <!-- Row 3: Research signals as inline chips -->
            <div v-if="contact.researchItemIds?.length > 0" class="px-4 py-1.5 border-t border-slate-50 bg-white flex items-center gap-1.5 flex-wrap">
                <span class="text-[10px] font-medium text-slate-400 uppercase tracking-wide mr-0.5">Signals:</span>
                <span v-for="itemId in contact.researchItemIds" :key="itemId"
                    class="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded-full"
                    :class="signalChipClass(itemId)">
                    <UIcon
                        :name="signalIcon(itemId)"
                        class="w-3 h-3 flex-shrink-0" />
                    <span class="truncate max-w-[150px]">{{ contact.researchItemNames?.[itemId] || `Signal #${itemId}` }}</span>
                </span>
            </div>

            <!-- Row 4: Message preview (sent or draft) -->
            <div v-if="displayMessages.length > 0" class="px-4 py-2 border-t border-slate-100 bg-white">
                <div v-if="!messagesExpanded"
                    class="flex items-center gap-2 px-3 py-2 rounded-md border cursor-pointer group transition-colors"
                    :class="isDraft ? 'border-amber-200 bg-amber-50 hover:bg-amber-100 hover:border-amber-300' : 'border-slate-200 bg-slate-50 hover:bg-slate-100 hover:border-slate-300'"
                    @click="messagesExpanded = true">
                    <UIcon :name="isLinkedIn ? 'i-bi-linkedin' : 'i-ion-mail-outline'" class="w-4 h-4 flex-shrink-0" :class="isDraft ? 'text-amber-500' : 'text-slate-500'" />
                    <span class="text-xs font-medium flex-shrink-0" :class="isDraft ? 'text-amber-700' : 'text-slate-700'">
                        {{ isDraft ? 'View draft' : `View ${isLinkedIn ? 'message' : 'email'} sent` }}
                    </span>
                    <span v-if="firstMessageSubject" class="text-xs text-slate-400 truncate flex-1">{{ firstMessageSubject }}</span>
                    <span class="text-[10px] text-slate-400 flex-shrink-0">{{ displayMessages.length }} step{{ displayMessages.length > 1 ? 's' : '' }}</span>
                    <UIcon name="i-heroicons-chevron-down-20-solid" class="w-4 h-4 flex-shrink-0" :class="isDraft ? 'text-amber-400 group-hover:text-amber-600' : 'text-slate-400 group-hover:text-slate-600'" />
                </div>
                <div v-else class="rounded-md border overflow-hidden" :class="isDraft ? 'border-amber-200' : 'border-slate-200'">
                    <div class="flex items-center justify-between px-3 py-2 cursor-pointer transition-colors"
                        :class="isDraft ? 'bg-amber-50 hover:bg-amber-100' : 'bg-slate-50 hover:bg-slate-100'"
                        @click="messagesExpanded = false">
                        <span class="text-xs font-medium" :class="isDraft ? 'text-amber-700' : 'text-slate-600'">
                            {{ isDraft ? 'Draft' : (isLinkedIn ? 'Messages' : 'Email') + ' sent' }}
                            ({{ displayMessages.length }} step{{ displayMessages.length > 1 ? 's' : '' }})
                        </span>
                        <UIcon name="i-heroicons-chevron-up-20-solid" class="w-4 h-4 text-slate-400" />
                    </div>
                    <div class="divide-y divide-slate-100">
                        <div v-for="(msg, idx) in displayMessages" :key="idx" class="p-3 bg-white">
                            <div class="flex items-center gap-2 mb-1.5">
                                <span v-if="msg.subject" class="font-semibold text-sm text-slate-900">{{ msg.subject }}</span>
                                <span class="text-xs text-slate-400">
                                    Step {{ msg.stepIndex + 1 }}
                                    <span v-if="msg.stepType === 'linkedin_connection'" class="ml-0.5 text-blue-500 font-medium">· Connection Request</span>
                                    <span v-else-if="msg.stepType === 'linkedin_message'" class="ml-0.5 text-blue-500 font-medium">· LinkedIn Message</span>
                                    <span v-if="'sentAt' in msg && msg.sentAt" class="ml-0.5">· {{ formatAsRelativeDate(msg.sentAt as string) }}</span>
                                </span>
                            </div>
                            <div v-if="isLinkedIn && !msg.body?.trim()" class="text-slate-400 italic text-sm">Connection request (no message)</div>
                            <div v-else-if="isLinkedIn" class="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{{ msg.body }}</div>
                            <div v-else class="text-sm text-slate-700 email-body leading-relaxed" v-html="msg.body" @click="handlePreviewClick" />
                        </div>
                    </div>
                </div>
            </div>

            <!-- Row 5: Collapsible Role Play (hidden by default) -->
            <div v-if="rolePlay" class="border-t border-slate-100 bg-slate-50/30">
                <div class="px-4 py-1.5 flex items-center gap-2 cursor-pointer group select-none" @click="rolePlayExpanded = !rolePlayExpanded">
                    <UIcon name="i-heroicons-sparkles" class="w-3.5 h-3.5 text-purple-400 flex-shrink-0" />
                    <span class="text-xs font-medium text-purple-600 group-hover:text-purple-800">Show AI Role Play</span>
                    <UIcon
                        :name="rolePlayExpanded ? 'i-heroicons-chevron-up-20-solid' : 'i-heroicons-chevron-down-20-solid'"
                        class="w-3.5 h-3.5 text-purple-400 group-hover:text-purple-600" />
                </div>
                <div v-if="rolePlayExpanded" class="px-4 pb-3">
                    <div class="rounded border border-purple-100 bg-purple-50/50 p-3">
                        <div class="text-xs text-slate-700 prose prose-sm max-w-none roleplay-content" v-html="md.render(rolePlay)" />
                    </div>
                </div>
            </div>

            <!-- Legacy: fallback for old-format analysis (no structured verdict) -->
            <div v-if="legacyAnalysis" class="px-4 py-2 border-t border-slate-100 bg-slate-50/50">
                <div class="text-sm text-slate-700 prose prose-sm max-w-none analysis-content" v-html="md.render(legacyAnalysis)" />
            </div>
        </div>

        <!-- Empty state (completed but no data) -->
        <div v-else-if="isCompleted && !cardLoading" class="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
            <div class="flex items-center gap-2 text-sm text-slate-500">
                <UIcon name="i-heroicons-check-circle" class="w-4 h-4" />
                <span>No contact to review</span>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import markdownit from 'markdown-it';

const md = markdownit();
md.renderer.rules.link_open = function (tokens, idx, options, _env, self) {
    tokens[idx].attrSet('target', '_blank');
    tokens[idx].attrSet('rel', 'noopener noreferrer');
    return self.renderToken(tokens, idx, options);
};
const props = defineProps<{
    part: {
        type: string;
        toolCallId?: string;
        state?: string;
        input?: unknown;
        output?: unknown;
        errorText?: string;
        [key: string]: unknown;
    };
}>();

const isProcessing = computed(() => {
    if (props.part.output !== undefined) return false;
    if (props.part.errorText) return false;
    return true;
});

const isCompleted = computed(() =>
    props.part.output !== undefined && !props.part.errorText
);

const hasError = computed(() =>
    props.part.errorText !== undefined && props.part.errorText !== ''
);

interface StructuredOutput {
    assignmentId?: number;
    verdict?: 'would-reply' | 'would-skim' | 'would-delete';
    verdictReason?: string;
    angle?: string;
    suggestion?: string;
    rolePlay?: string;
    analysis?: string; // legacy fallback
    current?: number;
    total?: number;
}

const toolOutput = computed(() => {
    return props.part.output as StructuredOutput | undefined;
});

const toolInput = computed(() => {
    return props.part.input as { current?: number; total?: number; verdictReason?: string } | undefined;
});

const progressText = computed(() => {
    const c = toolOutput.value?.current ?? toolInput.value?.current;
    const t = toolOutput.value?.total ?? toolInput.value?.total;
    if (c && t) return `${c}/${t}`;
    return '';
});

// Structured fields
const verdict = computed(() => toolOutput.value?.verdict);
const verdictReason = computed(() => toolOutput.value?.verdictReason ?? '');
const angle = computed(() => toolOutput.value?.angle ?? '');
const suggestion = computed(() => toolOutput.value?.suggestion ?? '');
const rolePlay = computed(() => toolOutput.value?.rolePlay ?? '');

// Legacy fallback: if no structured verdict, show analysis as before
const legacyAnalysis = computed(() => {
    if (verdict.value) return ''; // structured mode, no legacy
    return toolOutput.value?.analysis ?? '';
});

const verdictLabel = computed(() => {
    switch (verdict.value) {
        case 'would-reply': return 'Would reply';
        case 'would-skim': return 'Would skim';
        case 'would-delete': return 'Would delete';
        default: return '';
    }
});

const verdictBadgeClass = computed(() => {
    switch (verdict.value) {
        case 'would-reply': return 'bg-green-100 text-green-800';
        case 'would-skim': return 'bg-amber-100 text-amber-800';
        case 'would-delete': return 'bg-red-100 text-red-800';
        default: return 'bg-slate-100 text-slate-600';
    }
});

const cardBorderClass = computed(() => {
    switch (verdict.value) {
        case 'would-reply': return 'border-green-200 bg-white';
        case 'would-skim': return 'border-amber-200 bg-white';
        case 'would-delete': return 'border-red-200 bg-white';
        default: return 'border-slate-200 bg-white';
    }
});

const inputAnalysisPreview = computed(() => {
    const input = props.part.input as { verdictReason?: string; analysis?: string } | undefined;
    return input?.verdictReason?.substring(0, 150) || input?.analysis?.substring(0, 150) || '';
});

// Fetch rich card data when tool output is available
import type { InternalApi } from "nitropack/types";
type ReviewCardData = NonNullable<InternalApi['/api/messages/analytics/review-cards']['post']>;
const cardLoading = ref(false);
const cardData = ref<ReviewCardData | null>(null);

watch(toolOutput, async (output) => {
    if (!output?.assignmentId) return;
    if (cardData.value?.assignmentId === output.assignmentId) return; // already fetched

    cardLoading.value = true;
    try {
        const data = await $fetch('/api/messages/analytics/review-cards', { method: 'POST', body: {
            assignmentId: output.assignmentId,
        } });
        cardData.value = data ?? null;
    } catch {
        cardData.value = null;
    } finally {
        cardLoading.value = false;
    }
}, { immediate: true });

const contact = computed(() => cardData.value);

const messagesExpanded = ref(false);
const rolePlayExpanded = ref(false);

const contactNameStr = computed(() => {
    const truth = contact.value?.truth as Record<string, string> | undefined;
    return truth?.['full-name'] || contact.value?.email || '';
});

const contactTitle = computed(() => {
    const truth = contact.value?.truth as Record<string, string> | undefined;
    return truth?.['current-title'] || '';
});

const companyName = computed(() => {
    const truth = contact.value?.prospectTruth as Record<string, string> | undefined;
    return truth?.['current-company-name'] || '';
});

const companyUrl = computed(() => {
    const truth = contact.value?.prospectTruth as Record<string, string> | undefined;
    return truth?.['current-company-url'] || null;
});

const linkedinUrl = computed(() => {
    const truth = contact.value?.truth as Record<string, string> | undefined;
    return truth?.['linkedin-url'] || null;
});

const isLinkedIn = computed(() =>
    contact.value?.channel === 'linkedin'
);

const isDraft = computed(() =>
    (contact.value?.messages?.length ?? 0) === 0 && (contact.value?.draftMessages?.length ?? 0) > 0
);

const displayMessages = computed(() =>
    (contact.value?.messages?.length ?? 0) > 0
        ? contact.value!.messages
        : contact.value?.draftMessages ?? []
);

function stripHtml(html: string): string {
    if (typeof document === 'undefined') return html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || div.innerText || '';
}

const firstMessageSubject = computed(() =>
    displayMessages.value?.[0]?.subject || ''
);

const firstMessagePreview = computed(() => {
    const msg = contact.value?.messages?.[0];
    if (!msg) return 'No preview';
    const body = msg.body || '';
    if (!body.trim() && isLinkedIn.value) return 'Connection request (no message)';
    const text = isLinkedIn.value ? body : stripHtml(body);
    return text.substring(0, 120).trim() || 'No preview';
});

function signalIcon(itemId: number): string {
    const q = contact.value?.researchQualification?.[itemId] as { qualified: boolean; error?: boolean } | undefined;
    if (q?.error) return 'i-ion-warning';
    if (q?.qualified) return 'i-ion-checkmark-circle';
    if (q && !q.qualified) return 'i-ion-close-circle';
    return 'i-ion-help-circle';
}

function signalChipClass(itemId: number): string {
    const q = contact.value?.researchQualification?.[itemId] as { qualified: boolean; error?: boolean } | undefined;
    if (q?.error) return 'bg-amber-50 text-amber-700';
    if (q?.qualified) return 'bg-green-50 text-green-700';
    if (q && !q.qualified) return 'bg-red-50 text-red-600';
    return 'bg-slate-100 text-slate-500';
}

function handlePreviewClick(event: MouseEvent) {
    const target = (event.target as HTMLElement).closest('a');
    if (target && target.tagName === 'A') {
        event.preventDefault();
        const href = target.getAttribute('href');
        if (href) {
            window.open(href, '_blank', 'noopener,noreferrer');
        }
    }
}
</script>

<style scoped>
.email-body :deep(p) {
    margin: 0.5em 0;
}
.email-body :deep(p:first-child) {
    margin-top: 0;
}
.email-body :deep(p:last-child) {
    margin-bottom: 0;
}
.email-body :deep(br) {
    display: block;
    content: '';
    margin-top: 0.25em;
}
.email-body :deep(a) {
    color: rgb(37 99 235);
    text-decoration: underline;
}
.email-body :deep(ul),
.email-body :deep(ol) {
    padding-left: 1.25rem;
    margin: 0.5em 0;
}
.email-body :deep(img) {
    display: none;
}
.analysis-content :deep(p) {
    margin: 0.25em 0;
}
.analysis-content :deep(ul),
.analysis-content :deep(ol) {
    padding-left: 1.25rem;
    margin: 0.25em 0;
}
.analysis-content :deep(li) {
    margin: 0.125em 0;
}
.analysis-content :deep(strong) {
    font-weight: 600;
    color: rgb(17 24 39);
}
.analysis-content :deep(blockquote) {
    border-left: 3px solid rgb(203 213 225);
    margin: 0.375em 0;
    padding: 0.25em 0.75em;
    color: rgb(71 85 105);
    background: rgb(248 250 252);
    border-radius: 0 0.25rem 0.25rem 0;
}
.analysis-content :deep(blockquote p) {
    margin: 0;
}
.analysis-content :deep(em) {
    color: rgb(100 116 139);
    font-style: italic;
}
.roleplay-content :deep(p) {
    margin: 0.25em 0;
}
.roleplay-content :deep(ul),
.roleplay-content :deep(ol) {
    padding-left: 1.25rem;
    margin: 0.25em 0;
}
.roleplay-content :deep(li) {
    margin: 0.125em 0;
}
.roleplay-content :deep(strong) {
    font-weight: 600;
    color: rgb(17 24 39);
}
</style>
